// The Delivery app's home: one consultant's month against forecast. Every
// figure is the signed-in person's own (or their projects' teams'), from
// /api/work/pacing; business days come from the server's calendar.
import {
  Avatar, BulletColumnChart, Button, Card, ChartCard, ChartHoverRows, ChartLegend, EmptyState, InlineMessage, MetricRow, OptionSelect,
  PaceIcon, PageContent, PageHeader, ProgressLineChart, Spinner, SummaryTable, Tab, TabList, Tabs,
  type BulletColumnItem, type ChartHoverRow, type SummaryTableRow,
} from '@olytic/foundation';
import { CapacityTable } from '@olytic/foundation/resourcing';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { authenticatedFetch } from '../auth/authClient';
import { useDocumentTitle } from '../documentTitle';
import { useLogTime, useLogTimeContext } from '../work/logTime';
import { usePhotos } from '../work/usePhotos';
import {
  addMonths, aheadMonths, aheadUnits, capacityOf, dayDate, holidayList, hours, labelUnits, monthLabel, monthPace, monthShort,
  pct, progressCallouts, progressSeries, pts, teamPace, tone, totals, type Unit, unitPace, type UnitPace,
} from './model';
import { type CapacityMonthRow, fetchPacing, fetchPacingMonth, type PacingHome, type PacingMonth, saveCapacity } from './pacingApi';

type TabKey = 'time' | 'projects' | 'assignments' | 'capacity' | 'calendar';
const TABS: readonly [TabKey, string][] = [
  ['time', 'Pacing'], ['projects', 'Projects'], ['assignments', 'Assignments'], ['capacity', 'Capacity'], ['calendar', 'Calendar'],
];
const TAB_KEY = 'mothership.delivery.homeTab';
const AHEAD = 6;
const ALL = '@all';

function rememberedTab(): TabKey {
  try {
    const v = window.localStorage.getItem(TAB_KEY);
    if (TABS.some(([k]) => k === v)) return v as TabKey;
  } catch {
    // Private windows: start on Pacing.
  }
  return 'time';
}

const h = (n: number) => `${hours(n)} h`;
const series = (color: string, label: string, mark: 'rect' | 'hatch' | 'target' | 'tick' | 'line' | 'dashed' | 'dotted') => ({ label, color, mark });

function ClockGlyph(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 7.5V12l3 2" />
    </svg>
  );
}
const Chevron = ({ back }: { back?: boolean }) => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
    <path d={back ? 'm10 3-5 5 5 5' : 'm6 3 5 5-5 5'} />
  </svg>
);

export function DeliveryHomePage(): JSX.Element {
  useDocumentTitle('My Delivery');
  const { open, savedAt } = useLogTime();
  useLogTimeContext(useMemo(() => ({ kind: 'cross' as const, label: 'My Delivery' }), []));
  const [data, setData] = useState<PacingHome | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>(rememberedTab);

  useEffect(() => {
    let live = true;
    fetchPacing(authenticatedFetch)
      .then((d) => live && setData(d))
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : 'Could not load your month.'));
    return () => {
      live = false;
    };
  }, [savedAt]);

  const pickTab = (key: string) => {
    setTab(key as TabKey);
    try {
      window.localStorage.setItem(TAB_KEY, key);
    } catch {
      // Not remembered; the tab still changes.
    }
  };

  const header = (
    <Card className="shrink-0 overflow-hidden">
      <PageHeader
        className="border-b-0"
        leading={<PaceIcon className="size-[46px] text-[var(--brand-accent)]" />}
        title="My Delivery"
        actions={(
          <Button variant="brand" onClick={() => open()} aria-haspopup="dialog">
            <ClockGlyph />
            Log time
          </Button>
        )}
      />
      <Tabs value={tab} onValueChange={pickTab}>
        <TabList className="border-b-0 px-6">
          {TABS.map(([key, label]) => <Tab key={key} value={key}>{label}</Tab>)}
        </TabList>
      </Tabs>
    </Card>
  );

  let body: ReactNode;
  if (error) body = <InlineMessage tone="error" role="alert">{error}</InlineMessage>;
  else if (!data) body = <div className="flex justify-center py-10"><Spinner label="Loading your month" /></div>;
  else if (!data.me) body = <InlineMessage tone="info">{data.error ?? 'You have no delivery figures yet.'}</InlineMessage>;
  else if (tab === 'time') body = <MyTime data={data} />;
  else if (tab === 'projects') body = <MyProjects data={data} />;
  else if (tab === 'assignments') body = <Assignments data={data} />;
  else if (tab === 'capacity') body = <MyCapacity data={data} onMonths={(months) => setData((d) => (d ? { ...d, months } : d))} />;
  else body = <Calendar data={data} />;

  return (
    <PageContent as="section" width="fill" className="gap-4 px-6 pb-8 pt-5">
      {header}
      {body}
    </PageContent>
  );
}

// ── Pacing ─────────────────────────────────────────────────────────────────

function useMonth(data: PacingHome) {
  const pace = useMemo(() => monthPace(data.current.days, data.today), [data]);
  const units = useMemo(() => labelUnits(data.current.units), [data]);
  return { pace, units };
}

/** The month My Time shows, loaded on demand; the current month comes with the home. */
function useMonthView(data: PacingHome) {
  const current = data.today.slice(0, 7);
  const [month, setMonth] = useState(current);
  const [views, setViews] = useState<Record<string, PacingMonth>>({ [current]: data.current });
  const [loadError, setLoadError] = useState<string | null>(null);
  useEffect(() => setViews((v) => ({ ...v, [current]: data.current })), [current, data.current]);

  const view = views[month];
  useEffect(() => {
    if (view) return undefined;
    let live = true;
    setLoadError(null);
    fetchPacingMonth(authenticatedFetch, month)
      .then((v) => live && setViews((all) => ({ ...all, [month]: v })))
      .catch((e: unknown) => live && setLoadError(e instanceof Error ? e.message : 'Could not load that month.'));
    return () => {
      live = false;
    };
  }, [month, view]);
  return { month, setMonth, view, loadError };
}

function hoverRows(p: UnitPace): (ChartHoverRow | null)[] {
  return [
    { label: 'Forecast', value: h(p.forecast) },
    { label: 'Billable', value: h(p.billable) },
    { label: 'On pace today', value: h(p.onPace) },
    null,
    { label: 'Attainment', value: p.attainment === null ? '—' : pct(p.attainment) },
    ...(p.pacing === null ? [] : [{ label: 'Pacing', value: pts(p.pacing), tone: tone(p.pacing) }]),
    { label: 'Needed per day', value: h(p.neededPerDay) },
  ];
}

function column(p: UnitPace, segments: BulletColumnItem['segments']): BulletColumnItem {
  return {
    key: p.unit.key,
    label: p.unit.label,
    segments,
    target: p.forecast || null,
    marker: p.forecast ? p.onPace : null,
    valueLabel: p.forecast ? `${hours(p.billable)} / ${h(p.forecast)}` : h(p.billable),
    valueLabelShort: h(p.billable),
    caption: p.attainment === null ? '—' : pct(p.attainment),
    captionNote: p.pacing === null ? undefined : pts(p.pacing),
    captionTone: p.pacing === null ? undefined : tone(p.pacing),
    description: `${p.unit.label} ${p.unit.name}: ${h(p.billable)} billable of ${h(p.forecast)} forecast`,
  };
}

function MyTime({ data }: { data: PacingHome }) {
  const { month, setMonth, view, loadError } = useMonthView(data);
  // While another month loads, the figures stay on the last one shown.
  const [shown, setShown] = useState(data.current);
  useEffect(() => {
    if (view) setShown(view);
  }, [view]);
  const pace = useMemo(() => monthPace(shown.days, data.today), [shown, data.today]);
  const t = totals(shown.units, pace);
  const metrics = (
    <MetricRow
      aria-label={monthLabel(shown.month)}
      surface="inverse"
      metrics={[
        { key: 'forecast', label: 'Forecast', value: hours(t.forecast), unit: 'h', sub: `${hours(t.confirmed)} confirmed · ${hours(t.assigned)} assigned` },
        { key: 'billable', label: 'Billable', value: hours(t.billable), unit: 'h', sub: `${hours(t.nonBillable)} h non-billable` },
        { key: 'elapsed', label: 'Month Elapsed', value: pct(pace.pct), sub: `${pace.elapsed} of ${pace.total} business days` },
        { key: 'attainment', label: 'Attainment', value: pct(t.attainment), sub: 'Billable ÷ forecast' },
        { key: 'pacing', label: 'Pacing', value: pts(t.pacing), sub: 'vs. standard pace', tone: tone(t.pacing) },
        { key: 'needed', label: 'Needed Per Day', value: hours(t.neededPerDay), unit: 'h', sub: `${pace.remaining} business days left` },
      ]}
    />
  );
  return <MonthlyProgress data={data} month={month} onMonth={setMonth} view={view} loadError={loadError} metrics={metrics} />;
}

function ByProject({ data }: { data: PacingHome }) {
  const { pace, units } = useMonth(data);
  const paces = units.map((u) => unitPace(u, u.forecast_hours, u.billable_hours, pace));
  return (
    <ChartCard
      title="By project"
      actions={<ChartLegend items={[series('var(--chart-1)', 'Billable', 'rect'), series('var(--brand-accent)', 'Forecast', 'target'), series('var(--fg-strong)', 'On pace today', 'tick')]} />}
    >
      {paces.length ? (
        <BulletColumnChart
          label="Your billable hours against forecast by project"
          items={paces.map((p) => column(p, [{ key: 'billable', value: p.billable }]))}
          format={(v) => `${Math.round(v)}`}
          tooltip={(it) => {
            const p = paces.find((x) => x.unit.key === it.key)!;
            return <ChartHoverRows title={`${p.unit.label} · ${p.unit.name}`} rows={hoverRows(p)} />;
          }}
        />
      ) : <EmptyState title="Nothing this month" description="No forecast or time on your projects this month." />}
    </ChartCard>
  );
}

function MonthlyProgress({ data, month, onMonth, view, loadError, metrics }: {
  data: PacingHome;
  metrics: ReactNode;
  month: string;
  onMonth: (month: string) => void;
  view: PacingMonth | undefined;
  loadError: string | null;
}) {
  const [unitKey, setUnitKey] = useState(ALL);
  const s = view ? progressSeries(view, data.today, unitKey === ALL ? null : unitKey) : null;
  const options = [{ value: ALL, label: 'All projects' }, ...labelUnits(view?.units ?? data.current.units).map((u) => ({ value: u.key, label: u.label }))];
  return (
    <ChartCard
      surface="inverse"
      title="Monthly progress"
      contentClassName="flex flex-col gap-4"
      actions={(
        <>
          <span className="flex items-center gap-1">
            <Button variant="subtle" size="icon-sm" aria-label="Previous month" disabled={month <= data.range.first} onClick={() => onMonth(addMonths(month, -1))}>
              <Chevron back />
            </Button>
            <span className="min-w-[120px] text-center text-sm font-emphasis text-[var(--fg-strong)]">{monthLabel(month)}</span>
            <Button variant="subtle" size="icon-sm" aria-label="Next month" disabled={month >= data.range.last} onClick={() => onMonth(addMonths(month, 1))}>
              <Chevron />
            </Button>
          </span>
          <ChartLegend items={[series('var(--accent-inverse)', 'Billable', 'line'), series('var(--fg-muted)', 'On pace', 'dashed'), series('var(--accent-inverse)', 'Current rate', 'dotted')]} />
          <OptionSelect aria-label="Project" options={options} value={unitKey} onValueChange={setUnitKey} className="w-56" />
        </>
      )}
    >
      {metrics}
      {loadError ? <InlineMessage tone="error" role="alert">{loadError}</InlineMessage> : null}
      {s ? (
        <ProgressLineChart
          label={`Cumulative billable hours in ${monthLabel(month)} against the on-pace line`}
          days={s.days}
          values={s.values}
          target={s.forecast}
          elapsed={s.elapsed}
          today={s.today}
          milestones={s.milestones}
          format={h}
          labels={{ value: 'Cumulative', target: 'Forecast' }}
          callouts={progressCallouts(s)}
        />
      ) : !loadError ? <div className="flex justify-center py-10"><Spinner label="Loading the month" /></div> : null}
    </ChartCard>
  );
}

// ── My Projects ───────────────────────────────────────────────────────────

function PersonName({ name, you, src }: { name: string; you: boolean; src: string | undefined }) {
  return (
    <span className="inline-flex items-center gap-2">
      <Avatar name={name} size="xs" {...(src ? { src } : {})} />
      {name}
      {you ? <span className="text-[var(--fg-muted)]">(you)</span> : null}
    </span>
  );
}

function MyProjects({ data }: { data: PacingHome }) {
  const { pace, units } = useMonth(data);
  const teams = teamPace(units, data.team, data.me!.id, pace);
  const photos = usePhotos(useMemo(() => {
    const seen = new Map<string, boolean>();
    for (const t of data.team) seen.set(t.app_user_id, t.has_photo);
    return [...seen].map(([id, has_photo]) => ({ id, has_photo }));
  }, [data.team]));
  const rows: SummaryTableRow[] = teams.flatMap((t) => [
    ...t.people.map((p, i): SummaryTableRow => ({
      key: `${t.unit.key}-${p.app_user_id}`,
      group: t.unit.key,
      cells: {
        project: i === 0 ? { code: t.unit.label, text: t.unit.name } : '',
        person: <PersonName name={p.name} you={p.you} src={photos.get(p.app_user_id)} />,
        forecast: h(p.forecast_hours),
        billable: h(p.billable_hours),
        attainment: p.attainment === null ? '—' : pct(p.attainment),
        pacing: p.pacing === null ? '—' : { text: pts(p.pacing), tone: tone(p.pacing) },
      },
    })),
    {
      key: `${t.unit.key}-team`,
      group: t.unit.key,
      total: true,
      cells: {
        project: '', person: 'Team', forecast: h(t.forecast), billable: h(t.billable),
        attainment: t.attainment === null ? '—' : pct(t.attainment),
        pacing: t.pacing === null ? '—' : { text: pts(t.pacing), tone: tone(t.pacing) },
      },
    },
  ]);
  if (!teams.length) return <Card><EmptyState title="No projects this month" description="Nothing is assigned to you or logged by you this month." /></Card>;
  return (
    <>
      <ByProject data={data} />
      <ChartCard
        title="Team attainment"
        actions={<ChartLegend items={[series('var(--chart-1)', 'You', 'rect'), series('var(--chart-1)', 'Teammates', 'hatch'), series('var(--brand-accent)', 'Team forecast', 'target'), series('var(--fg-strong)', 'On pace today', 'tick')]} />}
      >
        <BulletColumnChart
          label="Team billable hours against team forecast by project"
          height={320}
          items={teams.map((t) => column(t, [{ key: 'mine', value: t.mine }, { key: 'team', value: t.billable - t.mine, texture: 'hatch' }]))}
          tooltip={(it) => {
            const t = teams.find((x) => x.unit.key === it.key)!;
            return (
              <ChartHoverRows
                title={`${t.unit.label} · ${t.unit.name}`}
                rows={[
                  ...t.people.map((p) => ({ label: `${p.name}${p.you ? ' (you)' : ''}`, value: `${hours(p.billable_hours)} / ${h(p.forecast_hours)}` })),
                  null,
                  { label: 'Team', value: `${hours(t.billable)} / ${h(t.forecast)}` },
                  { label: 'Attainment', value: t.attainment === null ? '—' : pct(t.attainment) },
                  ...(t.pacing === null ? [] : [{ label: 'Pacing', value: pts(t.pacing), tone: tone(t.pacing) }]),
                ]}
              />
            );
          }}
        />
      </ChartCard>
      <ChartCard title="By person">
        <SummaryTable
          caption="Forecast and billable hours by project and person this month"
          columns={[
            { key: 'project', label: 'Project' }, { key: 'person', label: 'Person', numeric: false },
            { key: 'forecast', label: 'Forecast' }, { key: 'billable', label: 'Billable' },
            { key: 'attainment', label: 'Attainment' }, { key: 'pacing', label: 'Pacing' },
          ]}
          rows={rows}
        />
      </ChartCard>
    </>
  );
}

// ── Assignments ───────────────────────────────────────────────────────────

function Assignments({ data }: { data: PacingHome }) {
  const first = addMonths(data.today.slice(0, 7), 1);
  const months = aheadMonths(data.months, data.ahead, first, AHEAD);
  const projects = aheadUnits(data.ahead);
  const items: BulletColumnItem[] = months.map((m) => ({
    key: m.month,
    label: monthShort(m.month),
    segments: [{ key: 'assigned', value: m.assigned }],
    target: m.capacity || null,
    valueLabel: m.utilization === null ? '—' : `${Math.round(m.utilization)}%`,
    caption: `${hours(m.assigned)} / ${h(m.capacity)}`,
    description: `${monthLabel(m.month)}: ${h(m.assigned)} assigned of ${h(m.capacity)} available`,
  }));
  const offIn = (month: string) => [...new Set(data.holidays.filter((x) => x.date.startsWith(month)).map((x) => x.name))];
  const cols = [{ key: 'project', label: 'Project' }, ...months.map((m) => ({ key: m.month, label: monthShort(m.month) }))];
  const rows: SummaryTableRow[] = [
    ...projects.map((u) => ({
      key: u.key,
      cells: Object.fromEntries([
        ['project', { code: u.label, text: u.name }],
        ...months.map((m) => [m.month, m.byUnit.get(u.key) ? h(m.byUnit.get(u.key)!) : { text: '—', tone: 'muted' as const }]),
      ]),
    })),
    { key: 'assigned', total: true, cells: Object.fromEntries([['project', 'Assigned'], ...months.map((m) => [m.month, h(m.assigned)])]) },
    { key: 'available', cells: Object.fromEntries([['project', 'Available'], ...months.map((m) => [m.month, h(m.capacity)])]) },
    {
      key: 'utilization',
      total: true,
      cells: Object.fromEntries([['project', 'Utilization'], ...months.map((m) => [m.month, m.utilization === null ? '—' : { text: `${Math.round(m.utilization)}%`, tone: m.utilization > 100 ? 'negative' as const : undefined }])]),
    },
  ];
  return (
    <>
      <ChartCard
        title="Utilization"
        actions={<ChartLegend items={[series('var(--chart-1)', 'Assigned', 'rect'), series('var(--brand-accent)', 'Available', 'target')]} />}
      >
        <BulletColumnChart
          label="Assigned hours against available capacity by month"
          labelStyle="text"
          items={items}
          tooltip={(it) => {
            const m = months.find((x) => x.month === it.key)!;
            const off = offIn(m.month);
            return (
              <ChartHoverRows
                title={monthLabel(m.month)}
                rows={[
                  ...projects.filter((u) => m.byUnit.get(u.key)).map((u) => ({ label: u.label, value: h(m.byUnit.get(u.key)!) })),
                  null,
                  { label: 'Assigned', value: h(m.assigned) },
                  { label: 'Available', value: h(m.capacity) },
                  { label: 'Business days', value: String(m.businessDays) },
                  { label: 'Utilization', value: m.utilization === null ? '—' : `${Math.round(m.utilization)}%`, ...(m.utilization !== null && m.utilization > 100 ? { tone: 'negative' as const } : {}) },
                  ...(off.length ? [null, { label: off.join(', '), value: '' }] : []),
                ]}
              />
            );
          }}
        />
      </ChartCard>
      <ChartCard title="By project">
        <SummaryTable caption="Hours assigned to you by project and month" columns={cols} rows={rows} empty="Nothing is assigned to you in the coming months." />
      </ChartCard>
    </>
  );
}

// ── My Capacity ───────────────────────────────────────────────────────────

function MyCapacity({ data, onMonths }: { data: PacingHome; onMonths: (months: CapacityMonthRow[]) => void }) {
  const current = data.today.slice(0, 7);
  const [error, setError] = useState<string | null>(null);
  const change = useCallback((month: string, value: number | null) => {
    setError(null);
    saveCapacity(authenticatedFetch, month, value)
      .then((r) => onMonths(r.months))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not save your capacity.'));
  }, [onMonths]);
  const standing = data.me?.standing_capacity ?? null;
  return (
    <ChartCard title="My capacity">
      {error ? <InlineMessage tone="error" role="alert">{error}</InlineMessage> : null}
      <CapacityTable
        caption="Your capacity, assigned hours and utilization by month"
        months={data.months.map((m) => ({
          key: m.month,
          label: monthLabel(m.month),
          current: m.month === current,
          businessDays: m.business_days,
          capacity: capacityOf(m),
          defaultCapacity: m.generated_capacity,
          edited: m.edited,
          assigned: m.assigned,
        }))}
        onCapacityChange={change}
        resetTitle={(m) => {
          const row = data.months.find((x) => x.month === m.key);
          return standing === null || !row
            ? `Back to ${h(m.defaultCapacity)}`
            : `Back to ${h(m.defaultCapacity)} (${h(standing)} × ${row.business_days} of ${row.weekdays} weekdays)`;
        }}
      />
    </ChartCard>
  );
}

// ── Calendar ──────────────────────────────────────────────────────────────

const PERIOD_LABEL = { week: 'Week', month: 'Month', quarter: 'Quarter', year: 'Year' } as const;

function Calendar({ data }: { data: PacingHome }) {
  const list = holidayList(data.holidays, data.today);
  return (
    <>
      <MetricRow
        layout="wrap"
        aria-label="Business days"
        metrics={data.periods.map((p) => ({
          key: p.period,
          label: PERIOD_LABEL[p.period],
          value: `${p.elapsed} / ${p.total}`,
          sub: `${p.remaining} business days left`,
          progress: p.total ? p.elapsed / p.total : 0,
        }))}
      />
      <ChartCard title="Holidays">
        <SummaryTable
          caption="Company holidays in the next year"
          columns={[
            { key: 'date', label: 'Date' }, { key: 'name', label: 'Holiday', numeric: false },
            { key: 'days', label: 'Business Days Off' }, { key: 'away', label: 'Away' },
          ]}
          rows={list.map((x) => ({
            key: x.start,
            cells: {
              date: x.days > 1 ? `${dayDate(x.start)} – ${dayDate(x.end)}` : dayDate(x.start),
              name: { text: x.name, ...(x.days > 1 ? { note: `· back ${dayDate(x.back)}` } : {}) },
              days: String(x.days),
              away: `${x.away} ${x.away === 1 ? 'day' : 'days'}`,
            },
          }))}
          empty="No holidays in the next year."
        />
      </ChartCard>
    </>
  );
}

